import { Injectable, inject } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { EMPTY, Observable, Subject, forkJoin, merge, of, timer } from "rxjs";
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { RON_MONITOR_POLLING } from "../models/ron-monitor-polling.token";
import { RonMonitorDataSource } from "../models/ron-monitor.data-source";
import {
  BoardSegmentFilter,
  ClosingDetail,
  ClosingPill,
  NotaryLane,
  RonMonitorBoard,
  RonMonitorSummary,
} from "../models/ron-monitor.models";
import { isSameDay } from "../ron-closing-monitor.utils";
import { RonMonitorClock } from "../services/ron-monitor-clock.service";
import { RonMonitorVisibility } from "../services/ron-monitor-visibility.service";

export interface ClosingDetailEntry {
  packageId: number;
  detail: ClosingDetail | null;
  loading: boolean;
  error: string | null;
}

export type ClosingDetailMap = Record<number, ClosingDetailEntry>;

export interface RonMonitorState {
  date: Date;
  followingToday: boolean;
  segment: BoardSegmentFilter;
  searchTerm: string;
  board: RonMonitorBoard | null;
  loading: boolean;
  error: string | null;
  lastRefreshedAt: string | null;
  expandedPackageIds: number[];
  details: ClosingDetailMap;
}

function initialRonMonitorState(): RonMonitorState {
  return {
    date: inject(RonMonitorClock).now(),
    followingToday: true,
    segment: "all",
    searchTerm: "",
    board: null,
    loading: false,
    error: null,
    lastRefreshedAt: null,
    expandedPackageIds: [],
    details: {},
  };
}

function retainDetails(
  details: ClosingDetailMap,
  keptIds: number[]
): ClosingDetailMap {
  const keep = new Set(keptIds);
  const present = Object.keys(details).map(Number);
  if (present.every((packageId) => keep.has(packageId))) {
    return details;
  }

  const next: ClosingDetailMap = {};
  present.forEach((packageId) => {
    if (keep.has(packageId)) {
      next[packageId] = details[packageId];
    }
  });
  return next;
}

@Injectable()
export class RonMonitorStore extends ComponentStore<RonMonitorState> {
  private readonly dataSource = inject(RonMonitorDataSource);
  private readonly polling = inject(RON_MONITOR_POLLING);
  private readonly visibility = inject(RonMonitorVisibility);
  private readonly clock = inject(RonMonitorClock);
  private readonly refreshTrigger$ = new Subject<void>();
  private expansionEpoch = 0;

  readonly date$ = this.select((state) => state.date);
  readonly segment$ = this.select((state) => state.segment);
  readonly searchTerm$ = this.select((state) => state.searchTerm);
  readonly board$ = this.select((state) => state.board);
  readonly loading$ = this.select((state) => state.loading);
  readonly error$ = this.select((state) => state.error);
  readonly lastRefreshedAt$ = this.select((state) => state.lastRefreshedAt);
  readonly expandedPackageIds$ = this.select(
    (state) => state.expandedPackageIds
  );
  readonly details$ = this.select((state) => state.details);

  readonly summary$: Observable<RonMonitorSummary | null> = this.select(
    (state) => state.board?.summary ?? null
  );
  readonly lanes$: Observable<NotaryLane[]> = this.select(
    (state) => state.board?.lanes ?? []
  );
  readonly closings$: Observable<ClosingPill[]> = this.select(
    (state) => state.board?.closings ?? []
  );

  private readonly loadBoard = this.effect<void>((trigger$) =>
    trigger$.pipe(
      filter(() => this.visibility.isVisible()),
      tap(() => this.rollDateIfFollowingToday()),
      tap(() => this.patchState({ loading: true })),
      withLatestFrom(this.date$, this.segment$),
      switchMap(([, date, segment]) =>
        this.dataSource.getBoard(date, segment).pipe(
          tap((board) => this.applyBoard(board)),
          catchError((error: unknown) => {
            this.patchState({ loading: false, error: this.toMessage(error) });
            return EMPTY;
          })
        )
      )
    )
  );

  private readonly pollDetails = this.effect<number[]>((packageIds$) =>
    packageIds$.pipe(
      switchMap((packageIds) =>
        packageIds.length === 0
          ? EMPTY
          : this.detailTriggers().pipe(
              filter(() => this.visibility.isVisible()),
              tap(() => this.markInitialLoading(packageIds)),
              switchMap(() =>
                forkJoin(
                  packageIds.map((packageId) => this.detailRequest(packageId))
                )
              )
            )
      )
    )
  );

  private readonly fetchDetail = this.effect<number>((packageId$) =>
    packageId$.pipe(
      tap((packageId) => this.markRetrying(packageId)),
      mergeMap((packageId) =>
        this.detailRequest(packageId).pipe(
          takeUntil(this.collapsed(packageId))
        )
      )
    )
  );

  constructor() {
    super(initialRonMonitorState());
    this.loadBoard(this.triggers());
    this.pollDetails(this.expandedPackageIds$);
  }

  refresh(): void {
    this.refreshTrigger$.next();
  }

  setDate(date: Date): void {
    this.collapseAll();
    this.patchState({
      date,
      followingToday: isSameDay(date, this.clock.now()),
    });
    this.refresh();
  }

  setSegment(segment: BoardSegmentFilter): void {
    this.collapseAll();
    this.patchState({ segment });
    this.refresh();
  }

  setSearchTerm(searchTerm: string): void {
    this.patchState({ searchTerm });
  }

  toggleExpanded(packageId: number): void {
    if (this.get().expandedPackageIds.includes(packageId)) {
      this.collapse(packageId);
      return;
    }
    this.setExpanded([packageId]);
  }

  expand(packageId: number): void {
    const current = this.get().expandedPackageIds;
    if (current.includes(packageId)) {
      return;
    }
    this.setExpanded([...current, packageId]);
  }

  collapse(packageId: number): void {
    const current = this.get().expandedPackageIds;
    if (!current.includes(packageId)) {
      return;
    }
    this.setExpanded(current.filter((id) => id !== packageId));
  }

  collapseAll(): void {
    if (this.get().expandedPackageIds.length === 0) {
      return;
    }
    this.setExpanded([]);
  }

  retryDetail(packageId: number): void {
    if (!this.get().expandedPackageIds.includes(packageId)) {
      return;
    }
    this.fetchDetail(packageId);
  }

  private setExpanded(packageIds: number[]): void {
    this.expansionEpoch++;
    this.patchState({
      expandedPackageIds: packageIds,
      details: retainDetails(this.get().details, packageIds),
    });
  }

  private applyBoard(board: RonMonitorBoard): void {
    const state = this.get();
    const expandedPackageIds = this.stillPresent(
      state.expandedPackageIds,
      board
    );

    if (expandedPackageIds !== state.expandedPackageIds) {
      this.expansionEpoch++;
    }

    this.patchState({
      board,
      loading: false,
      error: null,
      lastRefreshedAt: board.generatedAt,
      expandedPackageIds,
      details: retainDetails(state.details, expandedPackageIds),
    });
  }

  private stillPresent(
    packageIds: number[],
    board: RonMonitorBoard
  ): number[] {
    if (packageIds.length === 0) {
      return packageIds;
    }

    const present = new Set(board.closings.map((closing) => closing.packageId));
    const kept = packageIds.filter((packageId) => present.has(packageId));
    return kept.length === packageIds.length ? packageIds : kept;
  }

  private detailRequest(packageId: number): Observable<void> {
    const epoch = this.expansionEpoch;

    return this.dataSource.getDetail(packageId).pipe(
      tap((detail) => this.applyDetail(packageId, detail, epoch)),
      map((): void => undefined),
      catchError((error: unknown) => {
        this.applyDetailError(packageId, error, epoch);
        return of<void>(undefined);
      })
    );
  }

  private applyDetail(
    packageId: number,
    detail: ClosingDetail,
    epoch: number
  ): void {
    this.patchDetail(
      packageId,
      {
        packageId,
        detail,
        loading: false,
        error: null,
      },
      epoch
    );
  }

  private applyDetailError(
    packageId: number,
    error: unknown,
    epoch: number
  ): void {
    this.patchDetail(
      packageId,
      {
        packageId,
        detail: this.get().details[packageId]?.detail ?? null,
        loading: false,
        error: this.toDetailMessage(error),
      },
      epoch
    );
  }

  private markInitialLoading(packageIds: number[]): void {
    const state = this.get();
    const pending = packageIds.filter(
      (packageId) =>
        state.expandedPackageIds.includes(packageId) &&
        !state.details[packageId]?.detail
    );
    if (pending.length === 0) {
      return;
    }

    const details: ClosingDetailMap = { ...state.details };
    pending.forEach((packageId) => {
      details[packageId] = {
        packageId,
        detail: null,
        loading: true,
        error: details[packageId]?.error ?? null,
      };
    });
    this.patchState({ details });
  }

  private markRetrying(packageId: number): void {
    const existing = this.get().details[packageId];

    this.patchDetail(
      packageId,
      {
        packageId,
        detail: existing?.detail ?? null,
        loading: true,
        error: existing?.error ?? null,
      },
      this.expansionEpoch
    );
  }

  private patchDetail(
    packageId: number,
    entry: ClosingDetailEntry,
    epoch: number
  ): void {
    const state = this.get();
    if (
      epoch !== this.expansionEpoch ||
      !state.expandedPackageIds.includes(packageId)
    ) {
      return;
    }
    this.patchState({ details: { ...state.details, [packageId]: entry } });
  }

  private rollDateIfFollowingToday(): void {
    const state = this.get();
    if (!state.followingToday) {
      return;
    }

    const today = this.clock.now();
    if (isSameDay(state.date, today)) {
      return;
    }

    this.patchState({ date: today });
  }

  private triggers(): Observable<void> {
    return merge(
      timer(0, this.polling.boardMs).pipe(map((): void => undefined)),
      this.refreshTrigger$,
      this.becameVisible()
    );
  }

  private detailTriggers(): Observable<void> {
    return merge(
      timer(0, this.polling.detailMs).pipe(map((): void => undefined)),
      this.becameVisible()
    );
  }

  private becameVisible(): Observable<void> {
    return this.visibility.changes$.pipe(
      filter(() => this.visibility.isVisible())
    );
  }

  private collapsed(packageId: number): Observable<void> {
    return this.expandedPackageIds$.pipe(
      filter((packageIds) => !packageIds.includes(packageId)),
      map((): void => undefined)
    );
  }

  private toMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return "Unable to load the RON closing board.";
  }

  private toDetailMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return "Unable to load this closing.";
  }
}
